/**
 * "Click anywhere to refresh portal data" — NOT a page reload.
 *
 * One passive, capturing `pointerdown` listener asks TanStack Query to refetch
 * the ACTIVE server-state queries most likely to be stale: ticket lists/queues,
 * ticket counts, the open ticket's detail/timeline/attachments (all under the
 * `tickets` key root), dashboard reports and notifications. It never touches
 * the session, catalog, admin-config or notification-preference queries, never
 * calls `location.reload`, never resets a form (React state is untouched; only
 * server data is re-read) and never closes a dialog.
 *
 * Guards: leading-edge throttle (one refresh per `THROTTLE_MS`), nothing while a
 * mutation is in flight, nothing for a click inside (or while focus is in) a text
 * field / select / contenteditable, and nothing while a modal dialog is open — a
 * refetch there could change the ticket's available actions under the person's
 * hands. Skipped clicks do not use up the throttle window.
 */
import { useEffect } from 'react';
import { useQueryClient, type Query } from '@tanstack/react-query';

export const CLICK_REFRESH_THROTTLE_MS = 10_000;

/** Query-key roots that hold fast-changing server data. */
const REFRESHABLE_ROOTS = new Set(['tickets', 'reports', 'notifications']);

export function isRefreshableQuery(query: Pick<Query, 'queryKey'>): boolean {
  const [root, second] = query.queryKey;
  if (typeof root !== 'string' || !REFRESHABLE_ROOTS.has(root)) return false;
  // Per-person settings, not live data.
  return !(root === 'notifications' && second === 'preferences');
}

const TYPING_SELECTOR = 'input, textarea, select, [contenteditable=""], [contenteditable="true"], [role="textbox"]';

function isTextEntryActive(target: EventTarget | null): boolean {
  if (target instanceof Element && target.closest(TYPING_SELECTOR)) return true;
  const active = document.activeElement;
  if (!(active instanceof HTMLElement)) return false;
  if (active.matches(TYPING_SELECTOR) || active.isContentEditable) {
    // A focused checkbox/radio/button-type input is not "typing".
    if (active instanceof HTMLInputElement) return !['checkbox', 'radio', 'button', 'submit', 'reset', 'range', 'color', 'file'].includes(active.type);
    return true;
  }
  return false;
}

export function useClickRefresh(throttleMs: number = CLICK_REFRESH_THROTTLE_MS): void {
  const queryClient = useQueryClient();
  useEffect(() => {
    let last = Number.NEGATIVE_INFINITY;
    function onPointerDown(e: PointerEvent): void {
      if (queryClient.isMutating() > 0) return;
      if (isTextEntryActive(e.target)) return;
      if (document.querySelector('[aria-modal="true"]')) return;
      const now = Date.now();
      if (now - last < throttleMs) return;
      last = now;
      void queryClient.invalidateQueries({ predicate: isRefreshableQuery, refetchType: 'active' });
    }
    document.addEventListener('pointerdown', onPointerDown, { capture: true, passive: true });
    return () => document.removeEventListener('pointerdown', onPointerDown, { capture: true });
  }, [queryClient, throttleMs]);
}
